import React, { useEffect, useRef, useState } from 'react';
import { SearchPicker, Dropdown, Spinner } from '@optimizely/axiom';
import { truncate } from 'utils';

interface Entity {
  email?: string;
  id?: number;
  key: string;
  name: string;
}

interface EntitySearchDropdownProps {
  entities: Entity[];
  handleEntitySelection: (entities: Entity[]) => void;
  placeholder?: string;
  searchForEntities?: (params: { [key: string]: any }) => Promise<any>;
  selectedEntities: Entity[];
  showSelectClearAll?: boolean;
  type: string;
}

const SearchMultiSelectDropdown = ({
  entities,
  placeholder = `Search for a Entity`,
  handleEntitySelection,
  searchForEntities,
  selectedEntities: externalSelectedEntities,
  showSelectClearAll = true,
  type,
}: EntitySearchDropdownProps) => {
  const [isDropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    setSelectedEntities(externalSelectedEntities);
  }, [externalSelectedEntities]);

  // Cleanup effect to handle unmount prevents memory leak caused by
  //state updates if the component unmounts before the async operations complete.
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const searchAvailableEntities = ({ query }: { query?: string }) => {
    return new Promise(resolve => setTimeout(resolve, 500)).then(() => {
      const availableEntitiesData = entities || [];
      const result = query
        ? {
            items: availableEntitiesData.filter(
              (entity: { email?: string; key: string; name: string }) =>
                entity.name.toLowerCase().includes(query.toLowerCase()) ||
                (entity.email &&
                  entity.email.toLowerCase().includes(query.toLowerCase()))
            ),
          }
        : { items: availableEntitiesData };
      return result.items;
    });
  };

  const searchFunction = ({ query }: { query: string }) => {
    if (searchForEntities) {
      return searchForEntities({
        per_page: 20,
        ...(query ? { query: query } : {}),
      }).then(entityResponse => {
        const items = entityResponse?.items || [];
        return items.filter(item => !item.archived);
      });
    } else {
      // We're fetching 'Users' from the permission-service's
      // 'permissions/projects/<projectID>/environments/<environmentID>' endpoint
      // which doesn't currently support searching via query string
      // so performing 'pseudo-search' by filtering intial response...
      return new Promise((resolve, reject) => {
        searchAvailableEntities({ query })
          .then(items => {
            if (isMountedRef.current) {
              resolve(items);
            }
          })
          .catch(error => {
            if (isMountedRef.current) {
              reject(error);
            }
          });
      });
    }
  };

  const handleEntityClick = (entity: Entity) => {
    const alreadySelected = selectedEntities.find(
      selectedEntity => selectedEntity.key === entity.key
    );
    const updatedSelectedEntities = alreadySelected
      ? selectedEntities.filter(
          selectedEntity => selectedEntity.key !== entity.key
        )
      : [...selectedEntities, entity];

    setSelectedEntities(updatedSelectedEntities);
    handleEntitySelection(updatedSelectedEntities); // Pass selected entities to the parent handler
    // Keep dropdown open on selection
    setDropdownOpen(true);
  };

  const handleSelectClearAll = (availableEntities: Entity[]) => {
    const existingSelectedKeys = new Set(
      selectedEntities.map(entity => entity.key)
    );

    // Check if all currently available are already selected
    const allCurrentlyVisibleAreSelected = availableEntities.every(entity =>
      existingSelectedKeys.has(entity.key)
    );

    if (allCurrentlyVisibleAreSelected) {
      // If all are selected, remove only those that are in availableEntities
      const remainingEntities = selectedEntities.filter(
        entity => !availableEntities.some(avail => avail.key === entity.key)
      );

      setSelectedEntities(remainingEntities);
      handleEntitySelection(remainingEntities);
    } else {
      // Add only new ones, keeping already selected items
      const newSelectedEntities = [
        ...selectedEntities,
        ...availableEntities.filter(
          entity => !existingSelectedKeys.has(entity.key)
        ),
      ];

      setSelectedEntities(newSelectedEntities);
      handleEntitySelection(newSelectedEntities);
    }
    // Keep dropdown open on selection

    setDropdownOpen(true);
  };

  return (
    <SearchPicker
      key={'entity-search'}
      onItemSelected={(
        index: string | number,
        options: { [x: string]: Entity }
      ) => {
        if (index === 0) {
          handleSelectClearAll(Object.values(options));
          return;
        }
        handleEntityClick(options[index]);
      }}
      searchFunction={searchFunction}
      selectedEntityIds={selectedEntities.map(entity => entity.key)}
      supportedTypes={[type]}>
      {({
        availableEntities,
        currentFauxFocusIndex,
        isLoading,
        makeSearchRequest,
        renderInput,
        resultsText,
        searchQuery,
      }: {
        availableEntities: Entity[];
        currentFauxFocusIndex: number;
        isLoading: boolean;
        makeSearchRequest: (query: string) => Promise<Entity[]>; // Assuming this is a function
        renderInput: (props: {
          onFocus: () => void;
          placeholder: string;
        }) => React.ReactElement;
        resultsText: { summary: string };
      }) => (
        <Dropdown
          fullWidth={true}
          isOpen={isDropdownOpen}
          onRequestClose={() => {
            setDropdownOpen(false);
          }}
          renderActivator={(activatorProps: any) =>
            renderInput({
              ...activatorProps,
              onFocus: () => makeSearchRequest(searchQuery),
              placeholder,
            })
          }>
          <Dropdown.Contents className="width--1-1">
            {showSelectClearAll && availableEntities.length > 0 && (
              <Dropdown.ListItem ignoreToggle={true}>
                <Dropdown.BlockLink
                  hasFauxFocus={currentFauxFocusIndex === 0}
                  hideFocusCheckIcon={true}
                  onClick={() => {}}>
                  <div
                    className="axiom-typography--body underline "
                    onClick={() => handleSelectClearAll(availableEntities)}>
                    {availableEntities.every(entity =>
                      selectedEntities.some(
                        selected => selected.key === entity.key
                      )
                    )
                      ? 'Clear All'
                      : 'Select All'}
                  </div>
                </Dropdown.BlockLink>
              </Dropdown.ListItem>
            )}
            <Dropdown.ListItem ignoreToggle={true} role="separator">
              <span>{resultsText.summary}</span>
            </Dropdown.ListItem>
            {isLoading && (
              <div className="push flex flex-justified--center">
                <Spinner size="small" />
              </div>
            )}
            {!isLoading &&
              availableEntities.map((entity: Entity, index: any) => (
                <Dropdown.ListItem ignoreToggle={true} key={entity.key}>
                  {' '}
                  <Dropdown.BlockLink
                    hasFauxFocus={currentFauxFocusIndex === index}
                    isItemSelected={selectedEntities.some(
                      selectedEntity => selectedEntity.key === entity.key
                    )}
                    isMultiSelect={true}
                    onClick={() => handleEntityClick(entity)}>
                    {truncate(entity.name, 92)}
                  </Dropdown.BlockLink>
                </Dropdown.ListItem>
              ))}
          </Dropdown.Contents>
        </Dropdown>
      )}
    </SearchPicker>
  );
};

export default SearchMultiSelectDropdown;
